import { useState, useEffect } from "react";
import {
  Box,
  Modal,
  Fade,
  Backdrop,
  Typography,
  Button,
  Snackbar,
  CircularProgress,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { getCategories, setPreference, setCategoryPreferences } from "../../actions";
import Categories from "../categories/categories";
import "./exam-preferences-modal.scss";

export default function ExamPreferencesModal({ open, onComplete, mandatory = true }) {
  const [selectedExamType, setSelectedExamType] = useState([]);
  const [examTypeList, setExamTypeList] = useState([]);
  const [showExamTypeError, setShowExamTypeError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const dispatch = useDispatch();

  const categoriesData = useSelector((state) => state.categoriesList);

  useEffect(() => {
    if (open) {
      if (!categoriesData?.categoriesList || categoriesData?.categoriesList?.examType?.length === 0) {
        dispatch(getCategories());
      }
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (categoriesData && Object.keys(categoriesData)?.length) {
      setExamTypeList(categoriesData?.categoriesList?.examType || []);
    }
  }, [categoriesData]);

  // Without this, if the getCategories request finishes with no exam types
  // (network error -> categoriesList: null, or any response missing examType),
  // the mandatory modal shows "Loading exam types..." forever with no way out
  // (it can't be dismissed with nothing selected) — a dead end. Treat "request
  // finished (loading === false) but no exam types" as a failed load so we can
  // offer a retry. (loading is undefined before the first request, so this
  // never fires prematurely; read examType from the store to avoid a flash
  // while the local examTypeList state catches up.)
  const loadFailed =
    categoriesData?.loading === false &&
    !(categoriesData?.categoriesList?.examType?.length);

  const handleSubmit = async () => {
    if (selectedExamType.length === 0) {
      setShowExamTypeError(true);
      return;
    }

    setIsSubmitting(true);
    setShowExamTypeError(false);
    const userId = window.localStorage.getItem("userId");

    const categoryMap = {
      discipline: [],
      organSystem: [],
      examType: [...selectedExamType]
    };

    dispatch(setCategoryPreferences({ userId, categoryMap }));

    const preferenceData = {
      userId,
      body: {
        userAcknowledged: true,
        quizPreference: {
          saveCategoriesPreference: true
        }
      }
    };
    dispatch(setPreference(preferenceData));

    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccessMessage(true);
      setTimeout(() => {
        onComplete();
      }, 1500);
    }, 1000);
  };

  const handleExamTypeSelect = (quesArr) => {
    setSelectedExamType([...quesArr]);
    if (quesArr.length > 0) {
      setShowExamTypeError(false);
    }
  };

  return (
    <>
      <Modal
        className="exam-preferences-modal"
        open={open}
        onClose={(_, reason) => {
          if (mandatory && selectedExamType.length === 0) {
            setShowExamTypeError(true);
            return;
          }
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={open}>
          <Box className="modal-container">
            <Typography variant="h5" className="header">
              Select Your Exam Preferences
            </Typography>
            <Typography variant="body2" className="subheader">
              Please select at least one exam type to personalize your learning experience
            </Typography>
            {mandatory && (
              <Typography variant="caption" style={{ color: 'var(--error-text, #c62828)', marginTop: '8px', display: 'block', textAlign: 'center' }}>
                * This step is mandatory and cannot be skipped
              </Typography>
            )}

            <Box className="content-box">
              {examTypeList.length > 0 ? (
                <Categories
                  onExamTypeCatSelect={handleExamTypeSelect}
                  selectedExamType={selectedExamType}
                  examType={examTypeList}
                  isViewDisabled={false}
                  hideCategoryWhenEmpty={true}
                  hidePowerSearch={true}
                />
              ) : loadFailed ? (
                <Box className="loading-container">
                  <Typography color="error" style={{ marginBottom: '12px', color: 'var(--error-text, #c62828)' }}>
                    Couldn't load exam types. Please check your connection and try again.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => dispatch(getCategories())}
                  >
                    Retry
                  </Button>
                </Box>
              ) : (
                <Box
                  className="loading-container"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "14px",
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography>Loading exam types...</Typography>
                </Box>
              )}

              {showExamTypeError && (
                <Typography
                  variant="body2"
                  color="error"
                  className="error-message"
                  style={{ fontWeight: 'bold', marginTop: '10px' }}
                >
                  ⚠️ Please select at least one exam type to continue
                </Typography>
              )}
            </Box>

            <Box className="action-buttons">
              <Button
                variant="contained"
                color={selectedExamType.length > 0 ? "primary" : "default"}
                onClick={handleSubmit}
                disabled={isSubmitting || examTypeList.length === 0}
                className="submit-btn"
                style={{
                  opacity: selectedExamType.length === 0 ? 0.6 : 1,
                  cursor: selectedExamType.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? "Saving..." : selectedExamType.length === 0 ? "Select Exam Type to Continue" : "Continue"}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={1500}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box className="success-message">
          <Typography>Preferences saved successfully!</Typography>
        </Box>
      </Snackbar>
    </>
  );
}